import { DotsThreeIcon } from '@phosphor-icons/react';
import { Button, Menu, SearchField, Select } from '@skupreme/shared/ui';
import type { ReactNode } from 'react';
import { flagImages } from '../legacy-bridge';

export type FilterOption = {
  value: string;
  label: string;
  /** A region's code, for its flag. */
  region?: string;
  logo?: string;
};

/* One select on the toolbar: region, marketplace, status. */
export type MappingFilter = {
  id: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
};

const HIDE_INACTIVE = 'hide';
const SHOW_INACTIVE = 'show';

/**
 * M2 listing hygiene (Ely 11-01): the "Show inactive" choice of an unmapped
 * list. Off (the default) lists Active and unknown-status listings; on adds the
 * Inactive, ARCHIVED and UNLISTED ones.
 */
export const showInactiveFilter = (
  isOn: boolean,
  onChange: (isOn: boolean) => void,
): MappingFilter => ({
  id: 'inactive',
  label: 'Show inactive',
  value: isOn ? SHOW_INACTIVE : HIDE_INACTIVE,
  options: [
    { value: HIDE_INACTIVE, label: 'Active listings' },
    { value: SHOW_INACTIVE, label: 'Show inactive' },
  ],
  onChange: value => onChange(value === SHOW_INACTIVE),
});

export type MappingActionsProps = {
  filters: MappingFilter[];
  /** Controls the plain selects can't express, e.g. a multi-select. */
  children?: ReactNode;
  isLoading?: boolean;
  search: string;
  onSearch: (search: string) => void;
  /** The page's extra actions, behind a "more" button; none hides it. */
  moreActions?: MappingMoreAction[];
};

export type MappingMoreAction = {
  id: string;
  label: string;
  isDisabled?: boolean;
  onAction: () => void;
};

function leadingFor(option: FilterOption) {
  const src = option.region
    ? (flagImages[option.region.toUpperCase()] ?? flagImages.WW)
    : option.logo;

  return src ? (
    <img
      src={src}
      alt=""
      className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-contain"
    />
  ) : undefined;
}

/** A mapping page's filters, search and extra actions, on the tabs' row. */
export function MappingActions({
  filters,
  children,
  isLoading,
  search,
  onSearch,
  moreActions = [],
}: MappingActionsProps) {
  return (
    <>
      {filters.map(filter => (
        <Select
          key={filter.id}
          aria-label={filter.label}
          isDisabled={isLoading}
          value={filter.value}
          onChange={filter.onChange}
          options={filter.options.map(option => ({
            value: option.value,
            label: option.label,
            leading: leadingFor(option),
          }))}
          className="sk:w-48"
        />
      ))}
      {children}
      <SearchField
        aria-label="Search listings"
        placeholder="SKU, UPC, Title, ASIN..."
        value={search}
        onChange={onSearch}
        className="sk:w-64"
      />
      {moreActions.length ? (
        <Menu
          aria-label="More actions"
          trigger={
            <Button
              variant="pill"
              size="md"
              shape="circle"
              aria-label="More actions"
            >
              <DotsThreeIcon size={16} weight="bold" aria-hidden />
            </Button>
          }
          actions={moreActions.map(({ id, label, isDisabled }) => ({
            id,
            label,
            isDisabled,
          }))}
          onAction={id =>
            moreActions.find(action => action.id === id)?.onAction()
          }
        />
      ) : null}
    </>
  );
}
